import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Header } from "../../Header";
import { useLocation } from "react-router-dom";
import './VanRateReport.css';
import { local_url } from "../../Const";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

export const VanRateReport = () => {
  const location = useLocation();
  
  // State variables for filters
  const [sfAreaId, setSfAreaId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [channel, setChannel] = useState("VAN"); // Default to VAN
  const [businessDate, setBusinessDate] = useState(new Date());
  
  // State variables for data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [filterText, setFilterText] = useState('');
  
  // State variables for dropdowns
  const [sfAreas, setSfAreas] = useState([]);
  const [branches, setBranches] = useState([]);

  // Channel options
  const channelOptions = [
    { value: "VAN", label: "VAN" },
    { value: "MOC", label: "MOC" }
  ];

  // Get authentication token and user ID from localStorage
  const token = localStorage.getItem('AuthorizationToken');
  const userId = localStorage.getItem('userId');

  // Handle authentication error
  const handleAuthError = () => {
    setError("Authentication error. Please log in again.");
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/login";
  };

  // Format date for API calls (YYYYMMDD format)
  const formatDateForApi = (date) => {
    if (!date) return "";
    
    let formattedDate;
    
    if (typeof date === 'string') {
      formattedDate = new Date(date);
    } else {
      formattedDate = date;
    }
    
    if (formattedDate instanceof Date && !isNaN(formattedDate.getTime())) {
      const year = formattedDate.getFullYear();
      const month = String(formattedDate.getMonth() + 1).padStart(2, '0');
      const day = String(formattedDate.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    }
    
    return "";
  };

  // Format date for display (DD-MM-YYYY)
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid and not the SQL min date
      if (isNaN(date.getTime()) || 
          dateString === "1723-01-01T00:00:00" || 
          dateString === "1753-01-01T00:00:00" ||
          dateString.includes("0001-01-01")) {
        return ""; 
      }
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (e) {
      console.error("Error formatting date:", e);
      return "";
    }
  };

  // Handle business date change
  const handleDateChange = (newDate) => {
    setBusinessDate(newDate ? newDate.toDate() : new Date());
  };

  // Handle area change and reset branch
  const handleAreaChange = (areaId) => {
    setSfAreaId(areaId);
    setBranchId("");  // Reset branch when area changes
    setBranches([]); // Clear branches list
    
    // Fetch branches for the selected area
    if (areaId) {
      fetchBranches(areaId);
    }
  };

  // Fetch branches based on selected area
  const fetchBranches = async (areaId) => {
    if (!token || !areaId) {
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${local_url}/api/Company/GetBranchList/ALL/SEGMENT/Laziz`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error('Failed to fetch branches');
      }

      const data = await response.json();
      setBranches(data || []);
      
      // Auto-select first branch if available
      if (data && data.length > 0) {
        setBranchId(data[0].branchID);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setError("Failed to fetch branches. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch SF Areas when component mounts
  useEffect(() => {
    const fetchAreas = async () => {
      if (!token || !userId) {
        handleAuthError();
        return;
      }

      setLoading(true);
      
      try {
        const response = await fetch(`${local_url}/api/SFArea/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            handleAuthError();
            return;
          }
          throw new Error('Failed to fetch SF Areas');
        }

        const data = await response.json();
        setSfAreas(data || []);
        
        // Auto-select first area if available
        if (data && data.length > 0) {
          const firstAreaId = data[0].sfAreaId;
          setSfAreaId(firstAreaId);
          
          // Fetch branches for this area
          await fetchBranches(firstAreaId);
        }
      } catch (error) {
        console.error('Error fetching SF Areas:', error);
        setError("Failed to fetch SF Areas. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (token && userId) {
      fetchAreas();
    } else {
      handleAuthError();
    }
  }, [token, userId]);

  // Fetch report data when filters change
  const fetchReportData = async () => {
    if (!sfAreaId) {
      setError("Please select a Sales Force Area.");
      return;
    }

    if (!branchId) {
      setError("Please select a Branch.");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const formattedDate = formatDateForApi(businessDate);
      
      // Use BranchChannelRate API
      const url = `${local_url}/api/BranchChannelRate/ALL/${branchId}/${formattedDate}/${channel}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setReportData(data);
        if (data.length === 0) {
          setError("No data available for the selected filters.");
        }
      } else {
        console.error("Unexpected data format:", data);
        setError("Received invalid data format from server.");
        setReportData([]);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setError(`Failed to fetch report data: ${error.message}`);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on filter text
  const filteredData = reportData.filter(item => {
    if (!filterText) return true;
    
    const searchText = filterText.toLowerCase();
    return (
      (item.itemCode && item.itemCode.toLowerCase().includes(searchText)) || 
      (item.itemName && item.itemName.toLowerCase().includes(searchText)) ||
      (item.uniqueId && item.uniqueId.toLowerCase().includes(searchText))
    );
  });

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      setError("No data to export.");
      return;
    }
    
    try {
      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      
      // Create headers row - Updated to include Channel instead of RateType
      const headers = ["Sr. No.", "ItemCode", "ItemName", "Rate", "Channel", "AreaName", "Updated Date"];
      
      // Create metadata rows
      const metadata = [
        [`Business Date: ${formatDisplayDate(businessDate)}`],
        [`SF Area: ${sfAreas.find(area => area.sfAreaId === sfAreaId)?.sfAreaName || sfAreaId}`],
        [`Branch: ${branches.find(branch => branch.branchID === branchId)?.branchName || branchId}`],
        [`Channel: ${channel}`],
        [""] // Empty row for spacing
      ];
      
      // Convert the data to the format expected by xlsx
      const excelData = [
        ...metadata,
        headers,
        ...filteredData.map((item, index) => [
          index + 1,
          item.itemCode || '',
          item.itemName || '',
          typeof item.rate === 'number' ? item.rate : 0,
          item.rateType || '', // Added rateType column
          item.areaName || '',
          formatDisplayDate(item.fromDate) || ''
        ])
      ];
      
      // Create worksheet from data
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);
      
      // Set column widths - Updated for new column structure
      const columnWidths = [
        { wch: 8 },   // Sr. No.
        { wch: 12 },  // ItemCode
        { wch: 30 },  // ItemName
        { wch: 12 },  // Rate
        { wch: 10 },  // Channel (rateType)
        { wch: 20 },  // AreaName
        { wch: 12 },  // From Date
      ];
      
      worksheet['!cols'] = columnWidths;
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Branch Channel Rate");
      
      // Format date in filename using DDMMYYYY format
      const dateFormatted = formatDateForApi(businessDate);
      const sfAreaName = sfAreas.find(area => area.sfAreaId === sfAreaId)?.sfAreaName || sfAreaId;
      const branchName = branches.find(branch => branch.branchID === branchId)?.branchName || branchId;
      
      // Create filename
      const fileName = `branch_channel_rate_${sfAreaName}_${branchName}_${channel}_${dateFormatted}.xlsx`;
      
      // Generate and save the file
      XLSX.writeFile(workbook, fileName);
      
    } catch (error) {
      console.error("Error exporting Excel file:", error);
      setError("Failed to export Excel file.");
    }
  };

  // Enhanced Loader component
  const Loader = () => (
    <div className="horecaQualify-loader-container">
      <div className="horecaQualify-loader">
        <div className="horecaQualify-loader-circle"></div>
        <div className="horecaQualify-loader-circle"></div>
        <div className="horecaQualify-loader-circle"></div>
      </div>
      <p className="horecaQualify-loader-text">Loading data...</p>
    </div>
  );

  return (
    <>
      <Header />
      <div className="horecaQualify-container">
        {loading && <Loader />}

        {/* <h2 className="horecaQualify-title">Branch Channel Rate Report</h2> */}

        <div className="horecaQualify-controls">
          {/* SF Area Dropdown */}
          <FormControl variant="outlined" className="horecaQualify-formControl" style={{ minWidth: 180 }} required>
            <InputLabel id="sfarea-select-label">Area</InputLabel>
            <Select
              labelId="sfarea-select-label"
              value={sfAreaId}
              onChange={(e) => handleAreaChange(e.target.value)}
              label="Sales Force Area"
              disabled={loading}
            >
              {sfAreas.map((area) => (
                <MenuItem key={area.sfAreaId} value={area.sfAreaId}>
                  {area.sfAreaName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Branch Dropdown */}
          <FormControl variant="outlined" className="horecaQualify-formControl" style={{ minWidth: 180 }} required>
            <InputLabel id="branch-select-label">Branch</InputLabel>
            <Select
              labelId="branch-select-label"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              label="Branch"
              disabled={loading || !sfAreaId || branches.length === 0}
            >
              {branches.map((branch) => (
                <MenuItem key={branch.branchID} value={branch.branchID}>
                  {branch.branchName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Channel Dropdown */}
          <FormControl variant="outlined" className="horecaQualify-formControl" style={{ minWidth: 120 }} required>
            <InputLabel id="channel-select-label">Channel</InputLabel>
            <Select
              labelId="channel-select-label"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              label="Channel"
              disabled={loading}
            >
              {channelOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date Picker */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Business Date"
              value={dayjs(businessDate)}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
              disabled={loading}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: {
                    width: 150,
                    backgroundColor: '#F1F8E9',
                    '& .MuiOutlinedInput-root': {
                      height: 40,
                    }
                  },
                },
              }}
            />
          </LocalizationProvider>

          {/* Search Field */}
          <div className="horecaQualify-search">
            <input
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Search items..."
              className="horecaQualify-search-field"
              disabled={loading}
            />
          </div>
          
          {/* Buttons */}
          <div className="horecaQualify-buttons">
            <button
              onClick={fetchReportData}
              className="horecaQualify-button horecaQualify-load-button"
              disabled={loading || !sfAreaId || !branchId}
            >
              Load Report
            </button>

            <button
              onClick={exportToExcel}
              className="horecaQualify-button horecaQualify-export-button"
              disabled={loading || filteredData.length === 0}
            >
              Export to Excel
            </button>
          </div>
        </div>

        {error && <div className="horecaQualify-error-message">{error}</div>}

        <div className="horecaQualify-table-container">
          <table className="horecaQualify-report">
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>ItemCode</th>
                <th>ItemName</th>
                <th>Rate</th>
                <th>Channel</th>
                <th>Area Name</th>
                <th>Updated Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? "horecaQualify-row-even" : "horecaQualify-row-odd"}>
                    <td>{index + 1}</td>
                    <td>{item.itemCode || '-'}</td>
                    <td>{item.itemName || '-'}</td>
                    <td className="horecaQualify-currency">
                      ₹{typeof item.rate === 'number' 
                        ? item.rate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
                        : '0.00'}
                    </td>
                    <td>{item.rateType || '-'}</td>
                    <td>{item.areaName || '-'}</td>
                    <td>{formatDisplayDate(item.fromDate) || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="horecaQualify-no-data">
                    {reportData.length === 0 && !error 
                      ? "Click 'Load Report' to view data" 
                      : reportData.length === 0 && error 
                        ? "No data available" 
                        : "No data matches your search criteria"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

VanRateReport.propTypes = {
  // No specific props needed for this component
};

export default VanRateReport;